import { useCallback, useEffect } from "react";
import { useAuth } from "../keycloak";
import { useMatomoContext } from "./matomo-context";
import { MatomoService } from "./matomo-service";
import { hashUserId, isTestUser } from "./utilities";
import type {
	TrackEventParams,
	TrackPageViewParams,
	TrackSiteSearchParams,
} from "./types";

/**
 * Hook to access Matomo tracking functionality
 */
export function useMatomo() {
	const context = useMatomoContext();

	const trackPageView = useCallback(
		(params?: TrackPageViewParams) => {
			if (!context.isEnabled || !context.hasConsent) return;

			const tracker = context.tracker;
			if (!tracker) return;

			// Set custom dimensions if provided
			if (params?.customDimensions) {
				Object.entries(params.customDimensions).forEach(([id, value]) => {
					tracker.setCustomDimension(Number(id), String(value));
				});
			}

			// Set custom title if provided
			if (params?.documentTitle) {
				tracker.setDocumentTitle(params.documentTitle);
			}

			// Set custom URL if provided
			if (params?.href) {
				const sanitizedUrl = MatomoService.sanitizeUrl(params.href);
				tracker.setCustomUrl(sanitizedUrl);
			}

			// Track the page view
			tracker.trackPageView(params?.documentTitle);
		},
		[context.isEnabled, context.hasConsent, context.tracker]
	);

	const trackEvent = useCallback(
		(params: TrackEventParams) => {
			if (!context.isEnabled || !context.hasConsent) return;

			const tracker = context.tracker;
			if (!tracker) return;

			// Set custom dimensions if provided
			if (params.customDimensions) {
				Object.entries(params.customDimensions).forEach(([id, value]) => {
					tracker.setCustomDimension(Number(id), String(value));
				});
			}

			// Track the event
			tracker.trackEvent(
				params.category,
				params.action,
				params.name,
				params.value,
				params.customDimensions
			);
		},
		[context.isEnabled, context.hasConsent, context.tracker]
	);

	const trackSiteSearch = useCallback(
		(params: TrackSiteSearchParams) => {
			if (!context.isEnabled || !context.hasConsent) return;

			const tracker = context.tracker;
			if (!tracker) return;

			// Set custom dimensions if provided
			if (params.customDimensions) {
				Object.entries(params.customDimensions).forEach(([id, value]) => {
					tracker.setCustomDimension(Number(id), String(value));
				});
			}

			// Track site search
			tracker.trackSiteSearch(
				params.keyword,
				params.category,
				params.resultsCount
			);
		},
		[context.isEnabled, context.hasConsent, context.tracker]
	);

	const setUserId = useCallback(
		(userId: string | null) => {
			if (!context.isEnabled) return;

			const tracker = context.tracker;
			if (!tracker) return;

			if (userId) {
				tracker.setUserId(userId);
			} else {
				tracker.resetUserId();
			}
		},
		[context.isEnabled, context.tracker]
	);

	const trackGoal = useCallback(
		(goalId: number, customRevenue?: number) => {
			if (!context.isEnabled || !context.hasConsent) return;

			const tracker = context.tracker;
			if (!tracker) return;

			tracker.trackGoal(goalId, customRevenue);
		},
		[context.isEnabled, context.hasConsent, context.tracker]
	);

	const enableLinkTracking = useCallback(() => {
		if (!context.isEnabled || !context.hasConsent) return;

		const tracker = context.tracker;
		if (!tracker) return;

		tracker.enableLinkTracking(true);
	}, [context.isEnabled, context.hasConsent, context.tracker]);

	return {
		// Context state
		isLoading: context.isLoading,
		isEnabled: context.isEnabled,
		hasConsent: context.hasConsent,
		error: context.error,

		// Tracking methods
		trackPageView,
		trackEvent,
		trackSiteSearch,
		trackGoal,
		setUserId,
		enableLinkTracking,

		// Consent management
		giveConsent: context.giveConsent,
		removeConsent: context.removeConsent,
		optOut: context.optOut,
		optIn: context.optIn,

		// Direct tracker access (for advanced usage)
		tracker: context.tracker,
	};
}

/**
 * Hook to automatically track page views
 * Should be used in the root layout or router component
 */
export function usePageTracking() {
	const { trackPageView, isEnabled, hasConsent } = useMatomo();

	useEffect(() => {
		if (isEnabled && hasConsent) {
			trackPageView({
				documentTitle: document.title,
				href: window.location.href,
			});
		}
	}, [trackPageView, isEnabled, hasConsent]);
}

/**
 * Hook to track events easily
 */
export function useEventTracking() {
	const { trackEvent } = useMatomo();

	const track = useCallback(
		(category: string, action: string, name?: string, value?: number) => {
			trackEvent({ category, action, name, value });
		},
		[trackEvent]
	);

	return track;
}

/**
 * Hook to automatically track user identification
 * Links anonymous sessions to authenticated users
 */
export function useUserTracking() {
	const { setUserId, isEnabled, hasConsent } = useMatomo();
	const { authenticated, user } = useAuth();

	useEffect(() => {
		// Only track if Matomo is enabled and user has given consent
		if (!isEnabled || !hasConsent) {
			return;
		}

		async function updateUserId() {
			if (authenticated && user?.id) {
				// Check if this is a test user in production
				if (isTestUser(user.email)) {
					// Test users in production are tracked anonymously
					// They still generate events but without user identification
					console.log(
						"[Matomo] Test user detected in production, tracking anonymously"
					);
					setUserId(null);
				} else {
					// Regular users and all users in dev/staging get full tracking
					const hashedId = await hashUserId(user.id);
					setUserId(hashedId);
				}
			} else {
				// Clear user ID when not authenticated
				setUserId(null);
			}
		}

		void updateUserId();
	}, [authenticated, user, setUserId, isEnabled, hasConsent]);
}
